import { useParams } from "react-router-dom";

import { dailyTotalOf, dailyTotalSentence, toolUsageOf, type McpUsage, usageStatus } from "../../api/mcp";
import { Breadcrumbs } from "../../components/Layout";
import { NotSeenRecently, ResourceView } from "../../components/states";
import { Badge, Empty, Notice, Page, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";

/**
 * Today's credit spend, per tool.
 *
 * There is deliberately NO "Exhausted" column. The API's `exhausted` flag is
 * false for a GATED tool — which is strictly more restricted than an exhausted
 * one — so binding it directly would tell the operator the opposite of the
 * truth. `usageStatus()` is the only reader of that field.
 */
export function TenantUsage({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const path = `/mcp/admin/tenant/${encodeURIComponent(ref)}/usage`;
  const { state, reload } = useResource<McpUsage>(config, path, [ref]);

  return (
    <Page title={<span className="ref">{ref}</span>} subtitle="Today's credit spend.">
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref, to: `/tenants/${encodeURIComponent(ref)}` },
          { label: "Usage" },
        ]}
      />

      <ResourceView
        state={state}
        what="usage"
        onRetry={reload}
        notFound={<NotSeenRecently reference={ref} />}
      />

      {state.kind === "ready" &&
        (() => {
          const u = state.data;
          const daily = dailyTotalOf(u);
          const tools = Object.entries(toolUsageOf(u)).sort(
            ([, a], [, b]) => b.spent - a.spent,
          );
          const gated = tools.filter(([, t]) => t.cap === 0).length;
          const exhausted = tools.filter(([, t]) => usageStatus(t).kind === "exhausted").length;

          return (
            <>
              <div className="mb-4">
                <StatRow>
                  <Stat label="Credits today" value={u.total_credits ?? "—"} />
                  <Stat
                    label="Daily total"
                    value={typeof daily?.spent === "number" ? daily.spent : "—"}
                    hint={daily?.mode}
                  />
                  <Stat label="Tools used" value={tools.length} />
                  <Stat label="Exhausted" value={exhausted} tone={exhausted ? "amber" : "neutral"} />
                  <Stat label="Gated" value={gated} tone={gated ? "red" : "neutral"} />
                </StatRow>
                {dailyTotalSentence(u) && (
                  <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">{dailyTotalSentence(u)}</p>
                )}
              </div>

              {/* State the boundary rather than saying "today". An operator in
                  Riyadh reading this at 02:00 local is looking at yesterday. */}
              <Notice title={`Day: ${u.day}`}>
                Counters bucket on the UTC calendar day ({u.day_boundary}), matching the
                running server. Tier at time of read: <span className="ref">{u.tier}</span>.
              </Notice>

              {tools.length === 0 && (
                <Empty>
                  No credits spent today. Unlimited tools write nothing, so absence here does
                  not mean the merchant was idle.
                </Empty>
              )}

              {tools.length > 0 && (
                <Table
                  head={
                    <tr>
                      <Th>Tool</Th>
                      <Th>Status</Th>
                      <Th>Cap source</Th>
                    </tr>
                  }
                >
                  {tools.map(([name, t]) => {
                    const s = usageStatus(t);
                    return (
                      <tr key={name}>
                        <Td>
                          <span className="ref">{name}</span>
                        </Td>
                        <Td>
                          <Badge tone={s.tone}>{s.text}</Badge>
                        </Td>
                        <Td>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{t.cap_source}</span>
                        </Td>
                      </tr>
                    );
                  })}
                </Table>
              )}
            </>
          );
        })()}
    </Page>
  );
}
